import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@codes/ui-kit/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { updateAgentCachesForPromptUpdate } from '@web/app/features/agents/utils';
import { ChevronDown, Edit2, ScrollText, SquarePen } from 'lucide-react';

import { promptKeys, usePrompts } from '@codes/agents';
import { useUpdatePrompt } from '@codes/chatbots';
import { useGlobalLoader } from '@codes/shared';

import type { PromptView } from '@lemoncloud/ssocio-chatbots-api';

export const PromptSettings = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { setIsLoading } = useGlobalLoader();

    const [selectedPrompt, setSelectedPrompt] = useState<PromptView | null>(null);
    const [editingPrompt, setEditingPrompt] = useState<PromptView | null>(null);
    const [editingName, setEditingName] = useState('');
    const [editingText, setEditingText] = useState('');

    const { data: promptsData, isLoading, error, refetch } = usePrompts({ limit: -1 });
    const updatePrompt = useUpdatePrompt();

    const groupedPrompts = useMemo(() => {
        if (!promptsData?.data) {
            return { system: [], user: [] };
        }

        return promptsData.data.reduce(
            (acc, prompt) => {
                if (prompt.stereo === 'system') {
                    acc.system.push(prompt);
                }
                if (prompt.stereo === 'user') {
                    acc.user.push(prompt);
                }
                return acc;
            },
            { system: [] as PromptView[], user: [] as PromptView[] }
        );
    }, [promptsData?.data]);

    useEffect(() => {
        setIsLoading(isLoading);
        if (error) {
            toast({ title: t('ai.prompt.load_error') });
        }
    }, [isLoading, error, setIsLoading, t]);

    const handleEdit = (prompt: PromptView, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingPrompt(prompt);
        setEditingText(prompt.contents?.join('\n') || '');
        setEditingName(prompt.name || '');
    };

    const handleSave = async () => {
        if (!editingPrompt || !editingPrompt.id) {
            return;
        }

        try {
            await updatePrompt.mutateAsync(
                {
                    promptId: editingPrompt.id,
                    name: editingName,
                    contents: editingText.split('\n'),
                },
                {
                    onSuccess: async (updated: PromptView) => {
                        queryClient.setQueryData(promptKeys.list({ limit: -1 }), (oldData: any) => ({
                            ...oldData,
                            data: oldData.data.map((prompt: PromptView) =>
                                prompt.id === editingPrompt.id ? updated : prompt
                            ),
                        }));
                        if (editingPrompt.id) {
                            updateAgentCachesForPromptUpdate(queryClient, updated, editingPrompt.id);
                        }
                        toast({ title: t('ai.prompt.update_success') });
                        setEditingPrompt(null);
                    },
                }
            );
        } catch (error) {
            toast({ title: t('ai.prompt.update_error') });
        }
    };

    if (error) {
        return (
            <Button variant="outline" onClick={() => refetch()} className="w-full">
                <ScrollText className="h-4 w-4 mr-2" />
                {t('ai.prompt.reload')}
            </Button>
        );
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger className="focus-visible:outline-0 flex items-center justify-between p-2 bg-background border border-chatbot-line rounded-lg">
                    <div className="flex items-center text-sm">
                        <ScrollText className="h-4 w-4 mr-2" />
                        {t('ai.prompt.title')}
                    </div>
                    <ChevronDown className="h-5 w-5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-w-[200px]">
                    {isLoading ? (
                        <DropdownMenuItem disabled>{t('common.loading')}</DropdownMenuItem>
                    ) : promptsData?.data?.length === 0 ? (
                        <DropdownMenuItem disabled>{t('ai.prompt.no_prompts')}</DropdownMenuItem>
                    ) : (
                        <>
                            <div className="mb-1 text-sm font-semibold text-muted-foreground">
                                {t('ai.prompt.system_prompt')}
                            </div>
                            {groupedPrompts.system.map(prompt => (
                                <DropdownMenuItem
                                    key={prompt.id}
                                    onClick={() => setSelectedPrompt(prompt)}
                                    className="flex items-center justify-between gap-1 w-full"
                                >
                                    <span className="truncate">{prompt.name || t('ai.prompt.untitled')}</span>
                                    <div className="flex items-center">
                                        <span className="mx-2 text-[#F4F5F5]">|</span>
                                        <button className="group" onClick={e => handleEdit(prompt, e)}>
                                            <SquarePen size={16} className="text-sub group-hover:text-icon-hover" />
                                        </button>
                                    </div>
                                </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator className="my-[6px] bg-[#F4F5F5]"></DropdownMenuSeparator>
                            <div className="mb-1 text-sm font-semibold text-muted-foreground">
                                {t('ai.prompt.user_prompt')}
                            </div>
                            {groupedPrompts.user.map(prompt => (
                                <DropdownMenuItem
                                    key={prompt.id}
                                    onClick={() => setSelectedPrompt(prompt)}
                                    className="flex items-center justify-between gap-1 w-full"
                                >
                                    <span className="truncate">{prompt.name || t('ai.prompt.untitled')}</span>
                                    <div className="flex items-center">
                                        <span className="mx-2 text-[#F4F5F5]">|</span>
                                        <button className="group" onClick={e => handleEdit(prompt, e)}>
                                            <SquarePen size={16} className="text-sub group-hover:text-icon-hover" />
                                        </button>
                                    </div>
                                </DropdownMenuItem>
                            ))}
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={!!editingPrompt} onOpenChange={() => setEditingPrompt(null)}>
                <DialogContent>
                    <DialogTitle className="flex items-center gap-2 group px-6">
                        <input
                            type="text"
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            className="bg-transparent w-full text-center text-xl font-semibold focus:outline-none border-b border-transparent hover:border-border focus:border-gray-300 transition-colors"
                            placeholder={t('ai.prompt.untitled')}
                        />
                        <Edit2 className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </DialogTitle>
                    <div className="py-6 px-4">
                        <Textarea
                            value={editingText}
                            onChange={e => setEditingText(e.target.value)}
                            className="min-h-[600px] font-mono text-sm border"
                        />
                    </div>
                    <div className="flex items-center gap-2 justify-center">
                        <Button
                            variant="outline"
                            onClick={() => setEditingPrompt(null)}
                            disabled={updatePrompt.isPending}
                            className="flex-1"
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={updatePrompt.isPending} className="flex-1">
                            {updatePrompt.isPending ? (
                                <div className="flex items-center gap-2">
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                </div>
                            ) : (
                                t('common.save')
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={!!selectedPrompt} onOpenChange={() => setSelectedPrompt(null)}>
                <DialogContent showCloseButton={false} className="pt-0">
                    <DialogHeader>
                        <DialogTitle>{selectedPrompt?.name || t('ai.prompt.untitled')}</DialogTitle>
                    </DialogHeader>
                    <div className="p-4 overflow-auto">
                        <div className="whitespace-pre-wrap text-sm">{selectedPrompt?.contents?.join('\n')}</div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
